import {
  DICTAMEN_TEXTO,
  DictamenHomologacion,
  ESTADO_GATE_TEXTO,
  EstadoGate,
  EstatusHomologacion,
  VEREDICTO_TEXTO,
  Veredicto,
} from "../shared/model/iso42001/homologacion-modelo.model";

/*
 * Cómo se leen los números y los estados de Homologación de Modelos IA, igual
 * en la gestión, la consulta y la comparativa. Solo formato: COS no
 * recalcula ninguna calificación.
 */

/** Un número de verdad, o null: el servidor manda null donde no aplica. */
export function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** El delta tal como lo calculó el harness, con su signo. */
export function conSigno(v?: number | null, decimales = 1): string {
  const n = num(v);
  if (n === null) {
    return "—";
  }
  const texto = Math.abs(n).toFixed(decimales);
  return (n > 0 ? "+" : n < 0 ? "−" : "±") + texto;
}

export function signo(v?: number | null): "sube" | "baja" | "igual" {
  const n = num(v);
  return n === null || n === 0 ? "igual" : n > 0 ? "sube" : "baja";
}

/** Relativo del harness: 0.78× el del baseline. Menos es mejor en costo y latencia. */
export function veces(v?: number | null): string {
  const n = num(v);
  return n === null ? "" : `${n.toFixed(2)}×`;
}

export function tokensK(v?: number | null): string {
  const n = num(v);
  if (n === null) {
    return "—";
  }
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)} k` : String(n);
}

export function gateTexto(e: EstadoGate): string {
  return ESTADO_GATE_TEXTO[e] || e;
}

export function gateIcono(e: EstadoGate): string {
  switch (e) {
    case "pasa":
      return "check_circle";
    case "falla":
      return "cancel";
    case "indeterminado":
      return "help";
    default:
      return "remove_circle_outline";
  }
}

export function veredictoTexto(v?: Veredicto | null): string {
  return v ? VEREDICTO_TEXTO[v] || v : "—";
}

export function veredictoClase(v?: Veredicto | null): string {
  switch (v) {
    case "APROBADO":
    case "BASELINE":
      return "is-active";
    case "APROBADO_CON_RESERVAS":
    case "ESCALAR_A_DECISION_HUMANA":
    case "SIN_VEREDICTO":
      return "is-pending";
    case "RECHAZADO":
      return "is-danger";
    default:
      return "is-inactive";
  }
}

/** La misma coletilla que Evaluaciones IA Solser: el veredicto no es el dictamen. */
export function coletilla(estatus?: EstatusHomologacion | null): string {
  if (estatus === "FIRMADA") {
    return "· firmada";
  }
  if (estatus === "REEMPLAZADA") {
    return "· reemplazada";
  }
  return estatus ? "· sin dictamen" : "";
}

export function dictamenTexto(d?: DictamenHomologacion | null): string {
  return d ? DICTAMEN_TEXTO[d] || d : "";
}

export function dictamenClase(d?: DictamenHomologacion | null): string {
  switch (d) {
    case "ADOPTAR":
      return "is-active";
    case "ADOPTAR_CON_RESTRICCIONES":
      return "is-pending";
    case "NO_ADOPTAR":
      return "is-danger";
    default:
      return "is-inactive";
  }
}

/** «2026-09-22» o «2026-09-22 18:56» → «22/09/2026». Lo que no es fecha se deja tal cual. */
export function fechaDmy(v?: string | null): string {
  const m = typeof v === "string" ? /^(\d{4})-(\d{2})-(\d{2})/.exec(v) : null;
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v || "—";
}

/**
 * Una liga que escribió un tercero (la ficha del catálogo) solo si empieza
 * con https:// y no trae espacios ni comillas. Lo demás se pinta como texto
 * (spec 5.8): nunca javascript:, data: ni http://.
 */
export function esHttps(v?: string | null): boolean {
  return typeof v === "string" && /^https:\/\/[^\s"'<>\\]+$/.test(v);
}
